import { useState } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import InputField from 'dashboard/components/FormControls/InputField';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import * as Spacing from 'design-system/variables/spacing';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const validate = {
  name: [withMessage('Please enter a name', isRequired)],
  ticker: [withMessage('Please enter a valid ticker', isRequired)],
  website: [withMessage('Please enter a website', isRequired)],
  profile: [withMessage('Please enter a profile', isRequired)],
};

interface FormCreateReportBusinessSocialMediaProps {
  handleCancel: () => void;
  readOnly?: boolean;
}

enum CreateStrategy {
  Name = 'Name',
  Ticker = 'Ticker',
  Website = 'Website',
  Profile = 'Profile',
}

const StyledAccordion = styled(Accordion)`
  margin-bottom: ${Spacing.BASE}px;
`;

const FormCreateReportBusinessSocialMedia = ({
  submitting,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportBusinessSocialMediaProps &
  InjectedFormProps<
    FormCreateReportBusinessSocialMediaData,
    FormCreateReportBusinessSocialMediaProps
  >) => {
  const [strategy, setStrategy] = useState(CreateStrategy.Name);

  return (
    <>
      <StyledAccordion
        title='With Name'
        dataTest='name'
        onTitleClick={setStrategy.bind(null, CreateStrategy.Name)}
        overrideExpanded={strategy === CreateStrategy.Name}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            <Field
              id='create-report-business-social-media__name'
              label='Name'
              name='name'
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validate['name']}
              placeholder='Name'
              data-test='create-report-business-social-media__name'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-business-social-media__cancel'
              data-test='create-report-business-social-media__cancel'
              disabled={submitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-business-social-media__submit'
              data-test='create-report-business-social-media__submit'
              disabled={submitting}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
      <StyledAccordion
        title='With Ticker'
        dataTest='ticker'
        onTitleClick={setStrategy.bind(null, CreateStrategy.Ticker)}
        overrideExpanded={strategy === CreateStrategy.Ticker}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            <Field
              id='create-report-business-social-media__ticker'
              label='Ticker'
              name='ticker'
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validate['ticker']}
              placeholder='Ticker'
              data-test='create-report-business-social-media__ticker'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-business-social-media__cancel'
              data-test='create-report-business-social-media__cancel'
              disabled={submitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-business-social-media__submit'
              data-test='create-report-business-social-media__submit'
              disabled={submitting}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
      <StyledAccordion
        title='With Website'
        dataTest='website'
        onTitleClick={setStrategy.bind(null, CreateStrategy.Website)}
        overrideExpanded={strategy === CreateStrategy.Website}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            <Field
              id='create-report-business-social-media__website'
              label='Website'
              name='website'
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validate['website']}
              placeholder='Website'
              data-test='create-report-business-social-media__website'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-business-social-media__cancel'
              data-test='create-report-business-social-media__cancel'
              disabled={submitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-business-social-media__submit'
              data-test='create-report-business-social-media__submit'
              disabled={submitting}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
      <StyledAccordion
        title='With Profile'
        dataTest='profile'
        onTitleClick={setStrategy.bind(null, CreateStrategy.Profile)}
        overrideExpanded={strategy === CreateStrategy.Profile}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            <Field
              id='create-report-business-social-media__profile'
              label='Profile'
              name='profile'
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validate['profile']}
              placeholder='Profile'
              data-test='create-report-business-social-media__profile'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-business-social-media__cancel'
              data-test='create-report-business-social-media__cancel'
              disabled={submitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-business-social-media__submit'
              data-test='create-report-business-social-media__submit'
              disabled={submitting}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
    </>
  );
};

export interface FormCreateReportBusinessSocialMediaData {
  name?: string;
  ticker?: string;
  website?: string;
  profile?: string;
}

export const FORM_CREATE_REPORT_BUSINESS_SOCIAL_MEDIA_NAME =
  'formCreateReportBusinessSocialMediaData';

export default reduxForm<
  FormCreateReportBusinessSocialMediaData,
  FormCreateReportBusinessSocialMediaProps
>({
  form: FORM_CREATE_REPORT_BUSINESS_SOCIAL_MEDIA_NAME,
})(FormCreateReportBusinessSocialMedia);
